import type { ReactNode } from "react"
import { memo, useContext, useMemo, useState } from "react"
import { CNavSidebar, CTooltip, CConfirmModal } from "@shared/ui"
import { Link, useLocation } from "react-router"
import { useTranslation } from "react-i18next"
import {
  ACCOUNT_PAGE,
  CALENDAR_PAGE,
  PLANS_PAGE,
  RATIONS_PAGE,
  SHOP_PAGE,
  STATISTICS_PAGE,
  TRAININGS_PAGE,
} from "@shared/constants/navigation"
import { useQuery } from "@apollo/client/react"
import { Role, UserDocument } from "@/graphql/main/gql/graphql"
import { SidebarContext } from "@/app/providers/useSidebarContext"
import { useCloseOnEscape, useDevice } from "@shared/hooks"
import {
  LogoIcon,
  AthletesGroupIcon,
  CalendarSidebarIcon,
  AccountSidebarIcon,
  TrainingsSidebarIcon,
  PlansClipboardIcon,
  RationsSidebarIcon,
  SettingsSidebarIcon,
  ChartBarIcon,
  ShopSidebarIcon,
  SupportHelpIcon,
} from "@shared/icons"
import { useWatchConfirmSubmitContext } from "@/model"
import { useNavigate } from "react-router"
import { useMeContext } from "@/model/user/provider"
import { isSubscriptionValid } from "@/model/user/me"
import { useChatContext } from "@/app/providers/useChatContext"
import { AIIcon } from "@/shared/icons/AIIcon"
import { useChatwoot } from "@/model/support"
import { teShopAvailable } from "@/model/store/teShop"
import { useUpdateContext } from "@/components/UpdateNotification"
import { RefreshCw } from "lucide-react"

/**
 * One line of the mobile drawer: 44px tall, 10px radius, icon in a 22px box, 15/500 label.
 * The active line is the white chip — the whole row, not just the icon (docs/design/Настройки.dc.html).
 */
const MobileNavRow = ({ icon, label, active }: { icon: ReactNode; label: ReactNode; active?: boolean }) => (
  <div
    className={`flex min-h-11 items-center gap-3.5 rounded-[10px] px-2.5 transition-colors ${
      active ? "bg-[#FFFFFF]" : "hover:bg-[#3E3E3E]"
    }`}
  >
    <span className="flex w-[22px] flex-none justify-center">{icon}</span>
    <span className={`font-sfPro text-[15px] ${active ? "font-semibold text-[#101010]" : "font-medium text-white"}`}>
      {label}
    </span>
  </div>
)

export const MainSidebar = memo(({ closeSidebar }: { closeSidebar: () => void }) => {
  const sidebarContext = useContext(SidebarContext)

  if (!sidebarContext) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }

  const { isSidebarOpen } = sidebarContext
  const { setWatchConfirm, watchConfirm } = useWatchConfirmSubmitContext()
  const [isConfirmModal, setIsConfirmModal] = useState(false)
  const [link, setLink] = useState("")
  const navigate = useNavigate()
  const { me, currentRole } = useMeContext()
  const { openChat, closeChat } = useChatContext()
  const { open: openSupport, isEnabled: isSupportEnabled, isOpening: isSupportOpening } = useChatwoot()

  const handleOpenSupport = () => {
    closeChat()
    void openSupport()
  }
  const { isUpdateAvailable, isUpdating, triggerUpdate } = useUpdateContext()
  const userID = me.id
  const { t } = useTranslation()
  const { isCoach, get: defaultRole } = currentRole
  const { pathname } = useLocation()
  const { data: dataUsersBD } = useQuery(UserDocument, {
    variables: { userID },
    fetchPolicy: "cache-first",
    skip: !userID,
  })
  const isProAcc = isSubscriptionValid(dataUsersBD?.user?.permissions?.proUntil ?? "")

  const getIconColor = (href: string) => (pathname === href ? "#101010" : "white")

  const menuItems = [
    isCoach
      ? {
          role: Role.Trainer,
          renderIcon: (href: string) => <AthletesGroupIcon color={getIconColor(href)} />,
          href: "/",
          name: t("common.athletes"),
        }
      : {
          role: Role.Athlete,
          renderIcon: (href: string) => <CalendarSidebarIcon color={getIconColor(href)} />,
          href: "/",
          name: t("common.calendar"),
        },
    {
      role: Role.Athlete,
      renderIcon: (href: string) => <AccountSidebarIcon color={pathname.startsWith(href) ? "#101010" : "white"} />,
      href: "/account",
      name: t("common.account"),
    },
    {
      role: Role.Trainer,
      renderIcon: (href: string) => <TrainingsSidebarIcon color={getIconColor(href)} />,
      href: TRAININGS_PAGE,
      name: t("common.trainings"),
    },
    {
      role: null,
      renderIcon: (href: string) => <PlansClipboardIcon color={pathname === href ? "black" : "white"} />,
      href: PLANS_PAGE,
      name: t("calendar.plans"),
    },
    {
      role: null,
      renderIcon: (href: string) => <RationsSidebarIcon color={pathname.startsWith(href) ? "#101010" : "white"} />,
      href: RATIONS_PAGE,
      name: t("common.rations"),
    },
    {
      role: null,
      renderIcon: (href: string) => (
        <ChartBarIcon
          size={18}
          color={getIconColor(href)}
        />
      ),
      href: STATISTICS_PAGE,
      name: t("common.statistics"),
    },
    {
      role: null,
      renderIcon: (href: string) => <SettingsSidebarIcon color={pathname.startsWith(href) ? "#101010" : "white"} />,
      href: "/settings",
      name: t("common.settings"),
    },
    // The TE store's shop shows where teShopAvailable says: .ru hosts, the beta build, a dev build.
    ...(teShopAvailable()
      ? [
          {
            role: null,
            renderIcon: (href: string) => <ShopSidebarIcon color={getIconColor(href)} />,
            href: SHOP_PAGE,
            name: t("common.shop"),
          },
        ]
      : []),
  ]

  const { isDesktop, isMobile, isTouchDevice } = useDevice()

  // Only the mobile overlay traps the screen; on desktop the rail is always there to see.
  useCloseOnEscape(closeSidebar, isMobile && isSidebarOpen)
  const proAcc = useMemo(() => isProAcc, [isProAcc])
  return (
    <>
      <CConfirmModal
        isShow={isConfirmModal}
        onClose={() => setIsConfirmModal(false)}
        onSuccess={() => {
          // Set isSubmit and pendingNavigationUrl - actual navigation happens after successful save
          setWatchConfirm((prev) => ({ ...prev, isSubmit: true, pendingNavigationUrl: link }))
          setIsConfirmModal(false)
        }}
        title={t("common.saveChanges")}
        description={t("common.changesSavedText")}
        confirmText={t("common.save")}
        showDontSave
        onDontSave={() => {
          // Set isDontSave and pendingNavigationUrl - navigation happens after cleanup
          setWatchConfirm((prev) => ({ ...prev, isDontSave: true, pendingNavigationUrl: link }))
          setIsConfirmModal(false)
        }}
      />

      <div
        className={`${!isMobile ? "flex-shrink-0" : "fixed top-0  z-[999]"} ${isSidebarOpen ? "left-0" : isMobile && "-left-[999px]"} ${isCoach ? "border-l-[3px] border-l-[#ef4444]" : ""}`}
      >
        {isMobile && (
          <div
            // Not a control: with the mouse it repeats the closing Escape now does.
            role="presentation"
            onClick={closeSidebar}
            className="absolute top-0 left-0 w-screen h-screen min-h-screen bg-black/40"
          />
        )}

        <CNavSidebar
          className={`[&>div]:bg-[#101010] ${isSidebarOpen ? "[&>div]:pl-2.5" : "[&>div]:pl-1"} [&>div]:pt-3 [&>div]:pr-1 ${isMobile ? "[&>div]:px-2.5 [&>div]:pb-5" : ""} h-screen min-h-screen ${isMobile ? "!w-60" : "!w-[42px]"} transition-all relative z-[999]`}
          collapsed={!isMobile}
          collapseBehavior="collapse"
        >
          <Link
            to={CALENDAR_PAGE}
            className={`flex items-center gap-2.5 ${isMobile ? "mb-8" : "mb-10"}`}
          >
            {/* Collapsed rail: the box fills the 34px content column (42 minus the 4px gutters)
                and centres the 32px mark in it, the same column the 34px nav icons sit in.
                A fixed 40px box overflowed that column and pushed the logo off centre. */}
            <div
              className={`flex flex-none items-center justify-center ${isMobile ? "size-10 [&>svg]:size-10" : "h-10 w-full [&>svg]:size-8"}`}
            >
              <LogoIcon />
            </div>
            {!isDesktop && (
              <div
                className="font-sfPro text-base leading-[1.15] font-bold whitespace-nowrap"
                style={{ color: "#ffffff" }}
              >
                <div>Training</div>
                <div>Endurance</div>
              </div>
            )}
          </Link>

          <CNavSidebar.Items>
            <CNavSidebar.ItemGroup>
              {menuItems.map((item) => {
                return (
                  (defaultRole === item.role ||
                    !item.role ||
                    (proAcc && item.href !== TRAININGS_PAGE && item.href !== ACCOUNT_PAGE)) && (
                    <div
                      key={item.name}
                      className={isMobile ? "mb-1" : "mb-4"}
                    >
                      {isDesktop ? (
                        <CTooltip
                          content={item.name}
                          placement="right"
                          trigger="hover"
                          disabled={isTouchDevice}
                        >
                          <a
                            href={item.href}
                            onClick={(e) => {
                              e.preventDefault()
                              if (watchConfirm.isConfirmChange) {
                                setIsConfirmModal(true)
                                setLink(item.href)
                                return
                              }
                              navigate(item.href)
                            }}
                            className="block [&>li>div]:w-auto cursor-pointer group"
                          >
                            <CNavSidebar.Item
                              as="span"
                              icon={
                                <div
                                  className={`flex items-center justify-center min-w-[34px] min-h-[30px] rounded-[10px] text-white ${(item.href === "/" ? pathname === "/" : pathname.startsWith(item.href)) && "bg-[#FFFFFF]"}`}
                                >
                                  {item.renderIcon(item.href)}
                                </div>
                              }
                              className={`text-white hover:!bg-[unset] ${(item.href === "/" ? pathname === "/" : pathname.startsWith(item.href)) ? "[&>div]:hover:bg-[#FFFFFF] [&>div]:hover:rounded-[10px]" : "[&>div]:hover:bg-[#3E3E3E] [&>div]:hover:rounded-[10px]"} px-0 py-0`}
                            >
                              <span
                                className="inline-block ml-3 text-sm font-semibold font-sfPro"
                                style={{ color: "#ffffff" }}
                              >
                                {item.name}
                              </span>
                            </CNavSidebar.Item>
                          </a>
                        </CTooltip>
                      ) : (
                        <a
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault()
                            if (watchConfirm.isConfirmChange) {
                              setIsConfirmModal(true)
                              setLink(item.href)
                              return
                            }
                            navigate(item.href)
                            closeSidebar()
                          }}
                          className="block [&>li>div]:w-auto cursor-pointer group"
                        >
                          <MobileNavRow
                            active={item.href === "/" ? pathname === "/" : pathname.startsWith(item.href)}
                            icon={item.renderIcon(item.href)}
                            label={item.name}
                          />
                        </a>
                      )}
                    </div>
                  )
                )
              })}
            </CNavSidebar.ItemGroup>
          </CNavSidebar.Items>

          {/* Support button - available to everyone */}
          {isSupportEnabled && (
            <div className={isMobile ? "mt-3" : "mt-4"}>
              {isDesktop ? (
                <CTooltip
                  content={t("chat.supportCta")}
                  placement="right"
                  trigger="hover"
                  disabled={isTouchDevice}
                >
                  <button
                    onClick={handleOpenSupport}
                    disabled={isSupportOpening}
                    className="flex items-center w-full px-0 py-0 group cursor-pointer disabled:cursor-wait disabled:opacity-60"
                    aria-label={t("chat.supportCta")}
                  >
                    <div className="flex items-center justify-center min-w-[34px] min-h-[30px] rounded-[10px] hover:bg-[#3E3E3E] hover:rounded-[10px] transition-colors">
                      <SupportHelpIcon
                        size={22}
                        color="white"
                      />
                    </div>
                  </button>
                </CTooltip>
              ) : (
                <button
                  onClick={() => {
                    handleOpenSupport()
                    closeSidebar()
                  }}
                  disabled={isSupportOpening}
                  className="w-full text-left disabled:opacity-60"
                  aria-label={t("chat.supportCta")}
                >
                  <MobileNavRow
                    icon={
                      <SupportHelpIcon
                        size={22}
                        color="white"
                      />
                    }
                    label={t("chat.supportCta")}
                  />
                </button>
              )}
            </div>
          )}

          {/* AI Assistant button - for Pro users and trainers */}
          {(proAcc || isCoach) && (
            <div className="mt-4 pb-4">
              {isDesktop ? (
                <CTooltip
                  content="AI Assistant"
                  placement="right"
                  trigger="hover"
                  disabled={isTouchDevice}
                >
                  <button
                    onClick={() => openChat()}
                    className="flex items-center w-full px-0 py-0 group cursor-pointer"
                    aria-label="Open AI Assistant"
                  >
                    <div className="flex items-center justify-center min-w-[34px] min-h-[30px] rounded-[10px] hover:bg-[#3E3E3E] hover:rounded-[10px] transition-colors">
                      <AIIcon size={24} />
                    </div>
                  </button>
                </CTooltip>
              ) : (
                <button
                  onClick={() => {
                    openChat()
                    closeSidebar()
                  }}
                  className="w-full text-left"
                  aria-label="Open AI Assistant"
                >
                  <MobileNavRow
                    icon={<AIIcon size={24} />}
                    label="AI Assistant"
                  />
                </button>
              )}
            </div>
          )}

          {/* Update indicator - shows when new version is available */}
          {isUpdateAvailable && (
            <div className="pb-4">
              {isDesktop ? (
                <CTooltip
                  content={t("common.updateAvailable")}
                  placement="right"
                  trigger="hover"
                  disabled={isTouchDevice}
                >
                  <button
                    onClick={() => triggerUpdate()}
                    disabled={isUpdating}
                    className="flex items-center w-full px-0 py-0 group"
                    aria-label="Update application"
                  >
                    <div className="flex items-center justify-center min-w-[34px] min-h-[30px] rounded-[10px] bg-green-600 hover:bg-green-500 transition-colors">
                      <RefreshCw
                        size={16}
                        className={`text-white ${isUpdating ? "animate-spin" : ""}`}
                      />
                    </div>
                  </button>
                </CTooltip>
              ) : (
                <button
                  onClick={() => triggerUpdate()}
                  disabled={isUpdating}
                  className="flex items-center justify-start w-full px-0 py-0 group"
                  aria-label="Update application"
                >
                  <div className="flex items-center justify-center min-w-[34px] min-h-[30px] rounded-[10px] bg-green-600 hover:bg-green-500 transition-colors">
                    <RefreshCw
                      size={16}
                      className={`text-white ${isUpdating ? "animate-spin" : ""}`}
                    />
                  </div>
                  <span className="ml-3 text-sm font-semibold font-sfPro text-green-400">
                    {t("common.updateAvailable")}
                  </span>
                </button>
              )}
            </div>
          )}
        </CNavSidebar>
      </div>
    </>
  )
})
